import {
  Card,
  CardContent,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material'
import { DownloadFileNameLink, GreenText, OrderableTableCell } from 'components'
import { OhioTitleVehicleDto } from 'dtos/ohioTitles'
import { useState } from 'react'
import { clientSideOrderBy, clientSidePagination, formatDate, formatDateMoment } from 'utils'
import {
  OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL,
  ohioTitlesCompactCellSx,
  ohioTitlesDateCellSx,
  ohioTitlesDocumentCellSx,
  ohioTitlesMissingCellSx,
  ohioTitlesTableContainerSx,
  ohioTitlesTableSx
} from './ohioTitlesTableStyles'

interface OhioTitlesCardProps {
  selected: OhioTitleVehicleDto | undefined
  setSelected: React.Dispatch<React.SetStateAction<OhioTitleVehicleDto | undefined>>
  vehicles: OhioTitleVehicleDto[]
}

export default function OhioTitlesCard({ selected, setSelected, vehicles }: OhioTitlesCardProps) {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<keyof OhioTitleVehicleDto>('importerStockNumber')
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(10)

  const onChangeSortByDirection = (propertyName: keyof OhioTitleVehicleDto) => {
    setOrder(orderBy === propertyName && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(propertyName)
  }

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <TableContainer sx={ohioTitlesTableContainerSx}>
              <Table size='small' sx={ohioTitlesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'></TableCell>

                    <OrderableTableCell<OhioTitleVehicleDto>
                      keyName='importerStockNumber'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Importer Stock #
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='exporterCompany' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Exporter
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='buyerCompany' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Buyer
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='papsNumber' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      PAPS #
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto>
                      keyName='signedBOLDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Signed BOL
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='vin' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      VIN
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='year' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Year
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='make' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Make
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='model' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      Model
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto> keyName='_7501FileName' onChangeOrder={onChangeSortByDirection} order={order} orderBy={orderBy}>
                      7501
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto>
                      keyName='canOwnershipFileName'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      CAN Ownership
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto>
                      keyName='vehicleCheckInDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Vehicle Check-In
                    </OrderableTableCell>

                    <OrderableTableCell<OhioTitleVehicleDto>
                      keyName='sentToDOTDateTime'
                      onChangeOrder={onChangeSortByDirection}
                      order={order}
                      orderBy={orderBy}
                    >
                      Sent to DOT
                    </OrderableTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {vehicles
                    .sort(clientSideOrderBy<OhioTitleVehicleDto>(orderBy, order))
                    .slice(...clientSidePagination(page, pageSize))
                    .map((vehicle, index) => (
                      <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={selected?.vehicleGuid === vehicle.vehicleGuid}
                            disabled={!(vehicle._7501FileName && vehicle.canOwnershipFileName)}
                            indeterminate={!(vehicle._7501FileName && vehicle.canOwnershipFileName)}
                            onChange={() => setSelected(selected?.vehicleGuid === vehicle.vehicleGuid ? undefined : vehicle)}
                            size='small'
                          />
                        </TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.importerStockNumber ?? '-'}</TableCell>

                        <TableCell>{vehicle.exporterCompany}</TableCell>

                        <TableCell>{vehicle.buyerCompany ?? '-'}</TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.papsNumber ?? '-'}</TableCell>

                        <TableCell sx={ohioTitlesDateCellSx}>{vehicle.signedBOLDateTime ? formatDateMoment(vehicle.signedBOLDateTime) : '-'}</TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.vin}</TableCell>

                        <TableCell sx={ohioTitlesCompactCellSx}>{vehicle.year}</TableCell>

                        <TableCell>{vehicle.make}</TableCell>

                        <TableCell>{vehicle.model}</TableCell>

                        {vehicle._7501FileName ? (
                          <TableCell sx={ohioTitlesDocumentCellSx}>
                            <DownloadFileNameLink
                              fileName={vehicle._7501FileName}
                              label={OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL}
                            />
                          </TableCell>
                        ) : (
                          <TableCell sx={ohioTitlesMissingCellSx}>MISSING</TableCell>
                        )}

                        {vehicle.canOwnershipFileName ? (
                          <TableCell sx={ohioTitlesDocumentCellSx}>
                            <DownloadFileNameLink
                              fileName={vehicle.canOwnershipFileName}
                              label={OHIO_TITLES_DOCUMENT_DOWNLOAD_LABEL}
                            />
                          </TableCell>
                        ) : (
                          <TableCell sx={ohioTitlesMissingCellSx}>MISSING</TableCell>
                        )}

                        <TableCell sx={ohioTitlesDateCellSx}>
                          {vehicle.vehicleCheckInDateTime ? formatDate(vehicle.vehicleCheckInDateTime) : '-'}
                        </TableCell>

                        <TableCell sx={ohioTitlesDateCellSx}>
                          {vehicle.sentToDOTDateTime ? formatDateMoment(vehicle.sentToDOTDateTime) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      count={vehicles.length}
                      onPageChange={(_, page) => setPage(page)}
                      onRowsPerPageChange={e => setPageSize(e.target.value as unknown as number)}
                      page={page}
                      rowsPerPage={pageSize}
                      rowsPerPageOptions={[5, 10, 25, 50, 100]}
                      SelectProps={{ fullWidth: false }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**Ohio Titles Table:**

Vehicles appear in the Ohio Titles Table when the vehicle records are created, and the Title Processing State is set to Ohio.

**Check Box Selection:**

Allows users to select or deselect a vehicle.

Selecting a Vehicle adds the vehicle to the "Selected Vehicle" table section.

Deselecting a Vehicle removes the vehicle from the "Selected Vehicle" table section.

**7501 Column:**

Displays the 7501 Document associated with the vehicle.

Clicking the File Name Downloads the 7501 Document.

If the 7501 Document has not been uploaded for the vehicle the text "MISSING" will be displayed.

**CAN Ownership Column:**

Displays the CAN Ownership Document associated with the vehicle.

Clicking the File Name: Downloads the CAN Ownership Document.

If the CAN Ownership Document has not been uploaded for the vehicle the text "MISSING" will be displayed.`}
            </GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
